/**
 * Shared helpers for drawing dorm layouts and built-in keep-outs.
 *
 * Three SVG surfaces render dorm layouts — the wizard's own LayoutSVG
 * (DormWizardModal.jsx), the debug LayoutSVG (debug/dorm/LayoutSVG.jsx), and the
 * open-wall overlay — and each had grown its own copy of the same rotated-rect
 * corner math. This module is the one place that logic lives.
 */

import { calculateCorners } from '@shared-lib/bathroom-analyzer/pipeline/geometry';

/**
 * Footprint of a layout item as four world-space {x, z} corners.
 *
 * Prefers `physicalCorners` when the item carries them: those are the exact
 * polygon the optimizer placed and validated against, so drawing them keeps the
 * preview honest for anything whose footprint isn't a plain centered box.
 * Otherwise derives the rect from center + dimensions + rotation via the shared
 * `calculateCorners` primitive (same winding: back-left, back-right,
 * front-right, front-left).
 */
export function itemCorners(item) {
  if (Array.isArray(item.physicalCorners) && item.physicalCorners.length >= 3) {
    return item.physicalCorners;
  }
  return calculateCorners(
    item.center,
    item.dimensions?.x || 0,
    item.dimensions?.z || 0,
    item.rotation?.y || 0
  );
}

/**
 * Two world-space points marking which way an item faces: its center, and a
 * point `length` metres out along its front face normal.
 *
 * Derived from the drawn corners, NOT from `item.rotation.y`. The two things
 * that feed a dorm LayoutSVG disagree on rotation sign — the persisted/wire
 * shape stores `-atan2(dz, dx)` (see `rotationFromCorners` in
 * dorm-layout-generation.js, plus a quarter turn when `orientationSwapped`,
 * which is not on the wire at all), while the hand-built explainer adapters
 * pass the raw engine radian. An arrow computed from the angle points
 * backwards under one of them. The corner ordering is the same either way, so
 * this is convention-free and matches the polygon actually on screen.
 *
 * `calculateCorners` winds back-left, back-right, front-right, front-left, so
 * corners[2]->corners[3] is the front face. Verified to agree with the engine's
 * own `facingDirection` (scoring-helpers) on all four headings — that function
 * can't be used here because it needs `clearanceCorners`, which the wire shape
 * does not carry.
 *
 * Returns null for a footprint with no usable front edge.
 */
export function facingTick(item, length = 0.08) {
  const corners = itemCorners(item);
  if (!corners || corners.length < 4) return null;
  const mid = {
    x: (corners[2].x + corners[3].x) / 2,
    z: (corners[2].z + corners[3].z) / 2,
  };
  const dx = mid.x - item.center.x;
  const dz = mid.z - item.center.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) return null;
  // Starts ON the front face and points out, rather than running from the
  // centre. Two reasons, both learned by drawing it the other way first: the
  // label is rendered at the centre, so a centre-anchored tick struck through
  // the text of every item on the page and read as strikethrough rather than
  // as direction; and a fixed length measured from the centre escapes small
  // items entirely (a 12"-deep bookshelf sprouted a spike twice its own depth)
  // while vanishing inside big ones. Anchored to the face, the length means one
  // thing everywhere: how far the nose sticks out.
  return {
    from: { x: mid.x, z: mid.z },
    to: {
      x: mid.x + (dx / len) * length,
      z: mid.z + (dz / len) * length,
    },
  };
}

export const BUILTIN_KEEPOUT_TYPE = 'dorm-fixed-keepout';

/** True for a built-in box or its access strip, from any of the shapes we draw. */
export function isKeepoutItem(item) {
  return Boolean(item?.isKeepout || item?.type === BUILTIN_KEEPOUT_TYPE);
}

/**
 * Turn the server's `fixedObstacles` echo into draw items for a LayoutSVG.
 *
 * The optimizer strips built-ins from `layout.items` (they already exist as room
 * objects, so re-emitting them would duplicate them on apply), and returns them
 * alongside `doors`/`windows` instead. Rendering from the echoed
 * `clearanceCorners` rather than re-deriving the strip matters: the server keeps
 * the back edge on the wall and pushes only the front edge 18" inward, so a
 * locally-derived strip disagrees with it whenever the box isn't perfectly flush.
 *
 * Clearance first so the solid box draws over it.
 */
export function keepoutDisplayItems(fixedObstacles) {
  const out = [];
  for (const o of fixedObstacles || []) {
    // Only a DECLARED built-in has an access strip. A scan-derived obstacle is
    // body-only, so its clearance polygon is the body — drawing it would stack
    // an identical shape and a second label on top of the box, which is what
    // made a kept object read as unlabelled garble.
    const hasStrip =
      Array.isArray(o.clearanceCorners) &&
      o.clearanceCorners.length >= 3 &&
      !samePolygon(o.clearanceCorners, o.physicalCorners);
    if (hasStrip) {
      out.push({
        type: BUILTIN_KEEPOUT_TYPE,
        isClearance: true,
        center: o.center,
        dimensions: o.dimensions,
        rotation: o.rotation,
        physicalCorners: o.clearanceCorners,
      });
    }
    out.push({
      type: BUILTIN_KEEPOUT_TYPE,
      isKeepout: true,
      center: o.center,
      dimensions: o.dimensions,
      rotation: o.rotation,
      physicalCorners: o.physicalCorners,
      // What the scanner called it, and whether the student actually declared
      // it a built-in. Without these every obstacle drew as "built-in",
      // including a sofa nobody declared anything about.
      label: o.label || null,
      declaredBuiltIn: o.declaredBuiltIn !== false,
    });
  }
  return out;
}

/** Same four corners, within a millimetre. */
function samePolygon(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
    return false;
  }
  return a.every(
    (p, i) => Math.abs(p.x - b[i].x) < 0.001 && Math.abs(p.z - b[i].z) < 0.001
  );
}

/**
 * Keep-out styling, shared by the wizard's LayoutSVG and the debug one so the
 * two can't drift. Returns null for an ordinary item.
 */
export function keepoutStyle(item) {
  if (item?.isClearance) {
    return {
      fill: '#f59e0b',
      fillOpacity: 0.12,
      stroke: '#f59e0b',
      strokeOpacity: 0.6,
      strokeWidth: 1,
      strokeDasharray: '3,3',
      label: 'clearance',
      labelColor: '#f59e0b',
    };
  }
  if (!isKeepoutItem(item)) return null;

  // A scanned object the student asked us to leave alone is NOT a built-in.
  // Saying "built-in" for one is how a kept sofa turned into a mystery red box
  // nobody could trace — built-ins come from the built-ins editor, so a label
  // claiming one sends you looking somewhere it was never marked.
  if (item.declaredBuiltIn === false) {
    return {
      fill: '#8b5cf6',
      fillOpacity: 0.28,
      stroke: '#8b5cf6',
      strokeOpacity: 1,
      strokeWidth: 2,
      strokeDasharray: '4,3',
      label: item.label ? `${item.label} (kept)` : 'kept from scan',
      labelColor: '#fff',
    };
  }

  return {
    fill: '#ef4444',
    fillOpacity: 0.35,
    stroke: '#ef4444',
    strokeOpacity: 1,
    strokeWidth: item.selected ? 3 : 2,
    strokeDasharray: '4,3',
    label: item.selected ? 'built-in ✎' : 'built-in',
    labelColor: '#fff',
  };
}

/**
 * `mergeKeepoutsForApply` used to live here, and it was the lossiest of the
 * three apply merges it was meant to standardise: it kept ONLY keep-outs, so
 * every scanned object, user-added model and Style-It decor item in the room
 * was deleted. Harmless on the wizard's brand-new empty rooms; not harmless on
 * the debug page, which can be pointed at a real scanned room.
 *
 * Use `mergeObjectsForApply` from `@shared-lib/dorm-scan-inventory` instead —
 * the one sweep every apply path now shares (web relayout, the debug page, and,
 * mirrored in Swift, iOS), so they can no longer disagree about what an apply is
 * allowed to destroy.
 */
